import React from 'react';
import { Wallet, Mail, Building2, AlertCircle } from 'lucide-react';

/**
 * PaymentOptions
 *
 * Public, mobile-friendly payment methods block for the Book a Ride
 * and FAQ pages. Deliberately does NOT:
 *   - initiate or verify a Zelle transfer on the site
 *   - print a check mailing address (dispatch/invoice provides it)
 *   - claim ACH is active or expose account numbers
 *
 * `variant` values:
 *   - "full": full card (Book a Ride, FAQ)
 *   - "compact": one-line reference (Contact sidebar)
 */
export const PaymentOptions = ({ variant = 'full' }) => {
  if (variant === 'compact') {
    return (
      <div className="rounded-lg border-2 border-[#F9A825]/40 bg-[#FFF8E1] p-4" data-testid="payment-options-compact">
        <div className="flex items-start gap-3">
          <Wallet className="w-5 h-5 text-[#F9A825] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#004C97] leading-relaxed">
            <p className="font-semibold mb-1">Payment options</p>
            <p className="text-gray-700">
              Zelle and check may be accepted for eligible trips. ACH bank transfer is available after setup — ask
              dispatch. See <a href="/faq#payment-options" className="text-[#004C97] underline font-semibold hover:text-[#F9A825]" data-testid="payment-options-faq-link">payment options</a> for details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section id="payment-options" className="rounded-xl border-2 border-[#F9A825]/40 bg-white shadow-sm" data-testid="payment-options">
      <header className="bg-[#004C97] text-white rounded-t-xl px-5 py-3 flex items-center gap-2">
        <Wallet className="w-5 h-5 text-[#F9A825]" />
        <h3 className="text-base md:text-lg font-semibold">Payment Options</h3>
      </header>
      <div className="p-5 space-y-4 text-sm text-gray-700 leading-relaxed">
        <p>
          Payment arrangements and any required authorization are confirmed <strong>before the trip</strong>.
          Private-pay and authorized partner trips may be available. Contact dispatch at{' '}
          <a href="tel:+18329252128" className="text-[#004C97] font-semibold underline hover:text-[#F9A825]" data-testid="payment-options-dispatch-phone">
            (832) 925-2128
          </a>{' '}
          to confirm the payment method for your ride. The applicable amount and due date will be confirmed for your trip or shown on your invoice.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Zelle */}
          <div className="border-2 border-gray-100 rounded-lg p-4" data-testid="payment-option-zelle">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[#F9A825]" />
              <p className="font-semibold text-[#004C97]">Zelle</p>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              Use{' '}
              <span className="font-mono text-[#004C97] break-all" data-testid="payment-option-zelle-recipient">
                moveracareservices@gmail.com
              </span>{' '}
              — but <strong>confirm the recipient name with MoveraCare dispatch before sending</strong>. This site does not initiate or verify Zelle transfers.
            </p>
          </div>

          {/* Check */}
          <div className="border-2 border-gray-100 rounded-lg p-4" data-testid="payment-option-check">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#F9A825]" />
              <p className="font-semibold text-[#004C97]">Check</p>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              Make checks payable to <strong>MoveraCare Services</strong>. Delivery instructions are provided by your invoice or the dispatch team.
            </p>
          </div>

          {/* ACH */}
          <div className="border-2 border-gray-100 rounded-lg p-4" data-testid="payment-option-ach">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-[#F9A825]" />
              <p className="font-semibold text-[#004C97]">ACH bank transfer</p>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              <em>Ask dispatch about ACH availability.</em> ACH instructions will be shared once setup is complete — we do not publish routing or account numbers on this site.
            </p>
          </div>
        </div>

        <div className="rounded-md border-l-4 border-gray-400 bg-gray-50 px-3 py-2 flex items-start gap-2" data-testid="payment-options-note">
          <AlertCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-600 leading-relaxed">
            Submitting a ride request or completing this booking form does not confirm transportation or process a payment. Dispatch will confirm the trip, payment method, and any authorization before the ride.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PaymentOptions;
